import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStudentStats, submitAnswer, getAllGroupsTotals } from '../services/api';
import './Dashboard.css';

// Logging is disabled on or after this date (America/Los_Angeles)
const LOGGING_DISABLED_FROM = '2026-01-31';

function getTodayInLA() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
}

function isLoggingDisabled() {
  return getTodayInLA() >= LOGGING_DISABLED_FROM;
}

function Dashboard({ student, onLogout }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(student.stats || {});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [allGroups, setAllGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const loggingDisabled = isLoggingDisabled();

  useEffect(() => {
    // Refresh stats on mount
    refreshStats();
    // Load all groups for leaderboard
    loadAllGroups();
  }, []);

  // Also refresh when component becomes visible (handles browser back button)
  useEffect(() => {
    const handleFocus = () => {
      refreshStats();
      loadAllGroups();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const playCheeringSound = () => {
    try {
      const audio = new Audio('/cheering.mp3');
      audio.play().catch(error => {
        console.log('Could not play cheering sound:', error);
      });
      
      // Stop the audio after 10 seconds
      setTimeout(() => {
        audio.pause();
        audio.currentTime = 0;
      }, 10000);
    } catch (error) {
      console.log('Could not play cheering sound:', error);
    }
  };

  const playDisappointedSound = () => {
    try {
      const audio = new Audio('/disappointed.mp3');
      audio.play().catch(error => {
        console.log('Could not play disappointed sound:', error);
      });
    } catch (error) {
      console.log('Could not play disappointed sound:', error);
    }
  };

  const refreshStats = async () => {
    setLoading(true);
    try {
      const result = await getStudentStats(student.student_id);
      if (result.success && result.stats) {
        console.log('Refreshed stats:', result.stats);
        setStats(result.stats);
      } else {
        console.warn('Failed to refresh stats:', result);
      }
    } catch (error) {
      console.error('Error refreshing stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllGroups = async () => {
    setGroupsLoading(true);
    try {
      const result = await getAllGroupsTotals();
      if (result.success && result.groups) {
        // Include all groups (including the student's own group) in the leaderboard
        setAllGroups(result.groups);
      }
    } catch (error) {
      console.error('Error loading groups:', error);
    } finally {
      setGroupsLoading(false);
    }
  };

  const handleAnswer = async (answer) => {
    if (submitting || loggingDisabled) return;
    
    // Play appropriate sound based on answer
    if (answer === 'yes') {
      playCheeringSound();
    } else if (answer === 'no') {
      playDisappointedSound();
    }
    
    setSubmitting(true);
    try {
      const result = await submitAnswer(student.student_id, answer);
      
      if (result.success) {
        // Update stats immediately
        if (result.updatedStats) {
          console.log('Updated stats from backend:', result.updatedStats);
          setStats(result.updatedStats);
        } else {
          console.warn('No updatedStats in response, refreshing manually');
          refreshStats();
        }
        
        // Refresh leaderboard to show updated totals
        loadAllGroups();
        
        // Navigate to confirmation page
        navigate('/confirmation', {
          state: {
            answer: answer,
            stats: result.updatedStats || stats,
          },
        });
      } else {
        alert(result.error || 'Failed to submit answer. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatMinutes = (minutes) => {
    if (minutes < 60) {
      return `${minutes} minutes`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    }
    return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
  };

  const getBonusHours = (streak) => {
    // Bonus structure: 3 days = 1 hour, 6 days = 2 hours, 9 days = 3 hours, etc.
    // Continues indefinitely: 33 days = 11 hours, 36 days = 12 hours, etc.
    if (streak < 3) return 0;
    const milestoneDays = Math.floor(streak / 3) * 3;
    return milestoneDays / 3;
  };

  const isMilestoneDay = (streak) => {
    // Show message at milestone days: 3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36, 39, ...
    // Milestone days are multiples of 3
    return streak >= 3 && streak % 3 === 0;
  };

  const getStreakMessage = (streak) => {
    // Only show message at milestone days
    if (!isMilestoneDay(streak)) return null;
    
    const bonusHours = getBonusHours(streak);
    if (bonusHours === 0) return null;
    
    const hourText = bonusHours === 1 ? 'hour' : 'hours';
    return `🎉 You read ${streak} consecutive day${streak !== 1 ? 's' : ''} and added ${bonusHours} bonus ${hourText} to your advising group!`;
  };

  return (
    <div className="dashboard">
      <div className="dashboard-title-section">
        <h1 className="site-title">Winter Reading Streak</h1>
      </div>
      <div className="dashboard-header">
        <div className="header-content">
          <h2>Welcome, {student.student_name}!</h2>
          <button onClick={onLogout} className="logout-button">
            Logout
          </button>
        </div>
      </div>

      <div className="dashboard-content">
        {loading ? (
          <div className="loading">Loading stats...</div>
        ) : (
          <>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">📚</div>
                <div className="stat-value">{formatMinutes(stats.totalMinutes || 0)}</div>
                <div className="stat-label">Total Reading Time</div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">🔥</div>
                <div className="stat-value">{stats.currentStreak || 0}</div>
                <div className="stat-label">Day Streak</div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-value">{formatMinutes(stats.groupTotal || 0)}</div>
                <div className="stat-label">Group Total ({student.group_name})</div>
              </div>
            </div>

            {/* Bonus Message for Consecutive Reading */}
            {getStreakMessage(stats.currentStreak || 0) && (
              <div className="bonus-message">
                <p>{getStreakMessage(stats.currentStreak || 0)}</p>
              </div>
            )}

            {/* Leaderboard Section */}
            {allGroups.length > 0 && (
              <div className="leaderboard-section">
                <h2>📊 Groups Leaderboard</h2>
                <p className="leaderboard-subtitle">Average reading time per student</p>
                <div className="leaderboard-list">
                  {groupsLoading ? (
                    <div className="loading">Loading leaderboard...</div>
                  ) : (
                    allGroups.map((group, index) => (
                      <div key={group.groupName} className="leaderboard-item">
                        <div className="leaderboard-rank">
                          <span className="rank-number">#{index + 1}</span>
                        </div>
                        <div className="leaderboard-info">
                          <div className="leaderboard-group-name">
                            {group.groupName}
                            {group.groupName === student.group_name && ' (your group)'}
                          </div>
                          <div className="leaderboard-advisor">{group.advisor_name}</div>
                        </div>
                        <div className="leaderboard-stats">
                          <div className="leaderboard-minutes">{formatMinutes(Math.round(group.averageMinutes || 0))}</div>
                          <div className="leaderboard-students">{group.studentCount} student{group.studentCount !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            <div className="question-section">
              <h2>Did you read at least 30 minutes in the past 24 hours?</h2>
              <p className="question-subtitle">Track your reading progress</p>

              {loggingDisabled ? (
                <p className="logging-ended-message">Logging has ended for this program. You can still view your stats and the leaderboard above.</p>
              ) : (
                <>
                  <div className="answer-buttons">
                    <button
                      className="answer-button yes-button"
                      onClick={() => handleAnswer('yes')}
                      disabled={submitting}
                    >
                      <span className="button-emoji">✅</span>
                      <span className="button-text">Yes</span>
                    </button>

                    <button
                      className="answer-button no-button"
                      onClick={() => handleAnswer('no')}
                      disabled={submitting}
                    >
                      <span className="button-emoji">❌</span>
                      <span className="button-text">No</span>
                    </button>
                  </div>

                  {submitting && (
                    <div className="submitting">Submitting...</div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Dashboard;

